import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  ColorLens as ColorIcon,
  Label as LabelIcon,
  Payment as PaymentIcon,
  Assignment as ConsignmentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Description as ContractIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Speed as SpeedIcon,
  Category as CategoryIcon,
  ConfirmationNumber as ChassisIcon,
  LocalOffer as FipeIcon,
  EventNote as ModelYearIcon,
  Settings as GearIcon,
  PrecisionManufacturing as MotorIcon,
  StickyNote2 as PublicNoteIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { MaintenanceItem, MaintenancePriority, MaintenanceItemStatus, Vehicle, CONSIGNMENT_EXPOSURE_LABELS } from '../../types';
// @ts-ignore
import { format } from 'date-fns';
import vehicleService from '../../services/vehicleService';
import maintenanceItemService from '../../services/maintenanceItemService';
import { pdf } from '@react-pdf/renderer';
import VehicleMaintenanceFuturePDF from './VehicleMaintenanceFuturePDF';
import OlxPublishDialog from './OlxPublishDialog';
import { exportVehicleMaintenanceFutureReportExcel } from '../../utils/reportExcelExports';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { isConsignmentCommissionFixed } from '../../utils/consignmentCommission';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getDocumentFullUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = API_BASE.replace(/\/$/, '');
  return base ? `${base}${url.startsWith('/') ? url : `/${url}`}` : url;
};

const getDocumentIcon = (url: string, type?: string) => {
  const lower = (url || '').toLowerCase();
  const t = (type || '').toLowerCase();
  if (lower.includes('.pdf') || t === 'pdf') return 'pdf';
  if (lower.match(/\.(jpe?g|png|gif|webp)$/) || t === 'imagem' || t === 'foto') return 'image';
  return 'file';
};

const VehicleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isAdmin, canWrite } = useAuthUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [maintenanceItems, setMaintenanceItems] = useState<MaintenanceItem[]>([]);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [olxDialogOpen, setOlxDialogOpen] = useState(false);
  const [editingMaintenanceItem, setEditingMaintenanceItem] = useState<MaintenanceItem | null>(null);
  const [maintenanceForm, setMaintenanceForm] = useState<{
    description: string;
    estimated_amount: string;
    priority: MaintenancePriority;
    status: MaintenanceItemStatus;
    internal_notes: string;
  }>({
    description: '',
    estimated_amount: '',
    priority: 'medium',
    status: 'pending',
    internal_notes: '',
  });
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [downloadingMaintenancePdf, setDownloadingMaintenancePdf] = useState(false);
  const [downloadingMaintenanceExcel, setDownloadingMaintenanceExcel] = useState(false);

  useEffect(() => {
    const fetchVehicleDetails = async () => {
      try {
        setLoading(true);

        // Uso do serviço real em vez de dados mockados
        const data = await vehicleService.getVehicle(Number(id));
        setVehicle(data);
        try {
          const items = await maintenanceItemService.listByVehicle(Number(id));
          setMaintenanceItems(items);
        } catch (_) {
          // se o backend ainda não tiver o endpoint, não bloquear a tela
          setMaintenanceItems([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar detalhes do veículo:', error);
        setError('Não foi possível carregar os detalhes do veículo. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchVehicleDetails();
  }, [id]);

  const openNewMaintenance = () => {
    setEditingMaintenanceItem(null);
    setMaintenanceForm({
      description: '',
      estimated_amount: '',
      priority: 'medium',
      status: 'pending',
      internal_notes: '',
    });
    setMaintenanceDialogOpen(true);
  };

  const openEditMaintenance = (item: MaintenanceItem) => {
    setEditingMaintenanceItem(item);
    setMaintenanceForm({
      description: item.description || '',
      estimated_amount: item.estimated_amount != null ? String(item.estimated_amount) : '',
      priority: item.priority,
      status: item.status,
      internal_notes: item.internal_notes || '',
    });
    setMaintenanceDialogOpen(true);
  };

  const closeMaintenanceDialog = () => setMaintenanceDialogOpen(false);

  const saveMaintenance = async () => {
    if (!vehicle) return;
    if (!maintenanceForm.description.trim()) return;
    setSavingMaintenance(true);
    try {
      const payload: Partial<MaintenanceItem> = {
        description: maintenanceForm.description.trim(),
        estimated_amount: maintenanceForm.estimated_amount.trim() ? Number(maintenanceForm.estimated_amount) : null,
        priority: maintenanceForm.priority,
        status: maintenanceForm.status,
        internal_notes: maintenanceForm.internal_notes.trim() || null,
      };
      if (editingMaintenanceItem) {
        await maintenanceItemService.update(editingMaintenanceItem.id, payload);
      } else {
        await maintenanceItemService.create(vehicle.id, payload);
      }
      const items = await maintenanceItemService.listByVehicle(vehicle.id);
      setMaintenanceItems(items);
      setMaintenanceDialogOpen(false);
    } catch (e) {
      console.error(e);
      setError('Não foi possível salvar o item do checklist.');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const deleteMaintenance = async (item: MaintenanceItem) => {
    if (!window.confirm('Excluir este item do checklist?')) return;
    try {
      await maintenanceItemService.delete(item.id);
      if (vehicle) {
        const items = await maintenanceItemService.listByVehicle(vehicle.id);
        setMaintenanceItems(items);
      }
    } catch (e) {
      console.error(e);
      setError('Não foi possível excluir o item do checklist.');
    }
  };

  const convertToExpense = (item: MaintenanceItem) => {
    if (!vehicle) return;
    const params = new URLSearchParams();
    params.set('vehicle_id', String(vehicle.id));
    params.set('description', item.description);
    if (item.estimated_amount != null) params.set('amount', String(item.estimated_amount));
    navigate(`/despesas/novo?${params.toString()}`);
  };

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const getFullImageUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!API_BASE) return url;
    return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const getVehiclePrimaryImage = (v: Vehicle): string | null => {
    const first = (v.images || [])[0]?.url;
    return first ? getFullImageUrl(first) : null;
  };

  const downloadFutureMaintenancePdf = async () => {
    if (!vehicle) return;
    const future = maintenanceItems.filter((it) => it.status === 'pending' || it.status === 'in_progress');
    setDownloadingMaintenancePdf(true);
    try {
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      const doc = (
        <VehicleMaintenanceFuturePDF
          vehicle={vehicle}
          items={future}
          generatedAtLabel={generatedAtLabel}
          vehicleImageUrl={getVehiclePrimaryImage(vehicle)}
          logoUrl={logoUrl}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Manutencoes-futuras-${vehicle.brand}-${vehicle.model}-${String(vehicle.year)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o PDF de manutenções futuras.');
    } finally {
      setDownloadingMaintenancePdf(false);
    }
  };

  const downloadFutureMaintenanceExcel = async () => {
    if (!vehicle) return;
    const future = maintenanceItems.filter((it) => it.status === 'pending' || it.status === 'in_progress');
    setDownloadingMaintenanceExcel(true);
    try {
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      await exportVehicleMaintenanceFutureReportExcel({
        vehicle,
        items: future,
        generatedAtLabel,
      });
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o Excel de manutenções futuras.');
    } finally {
      setDownloadingMaintenanceExcel(false);
    }
  };

  const handleEditVehicle = () => {
    navigate(`/veiculos/editar/${id}`, { state: location.state });
  };

  const handleViewExpenses = () => {
    navigate(`/veiculos/${id}/despesas`);
  };

  const handleBackToList = () => {
    navigate(`/veiculos${(location.state as { listSearch?: string } | null)?.listSearch ?? ''}`);
  };

  // Formatar valores monetários
  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'; // Retorna um hífen se a data não existir

    try {
      const date = new Date(dateString);
      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        return '-';
      }
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-'; // Retorna um hífen em caso de erro
    }
  };

  const formatKmDisplay = (km?: string | null) => {
    if (km == null || !String(km).trim()) return '';
    const cleaned = String(km).replace(/\D/g, '');
    if (!cleaned) return String(km).trim();
    const withDots = cleaned
      .replace(/(\d)(\d{3})$/, '$1.$2')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${withDots} km`;
  };

  // Status do veículo em português
  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Disponível';
      case 'sold': return 'Vendido';
      case 'maintenance': return 'Em Manutenção';
      default: return status;
    }
  };

  // Cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return theme.palette.success.main;
      case 'sold': return theme.palette.info.main;
      case 'maintenance': return theme.palette.warning.main;
      default: return theme.palette.text.secondary;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
          Veículo não encontrado.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  const isMaintenance = (() => {
    const s = String((vehicle as any).status ?? '').toLowerCase().trim();
    // cobre variações comuns vindas do backend
    return s === 'maintenance' || s.includes('manut');
  })();

  const mainImageUrl =
    vehicle.images && vehicle.images.length > 0
      ? vehicle.images[0].url
      : undefined;

  const documents = (vehicle.documents || []) as any[];

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 2 }}
            color="inherit"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Detalhes do Veículo
          </Typography>
          {isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<MoneyIcon />}
              onClick={handleViewExpenses}
              sx={{ mr: 2, borderRadius: '8px' }}
            >
              Ver Despesas
            </Button>
          )}
          {canWrite && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOlxDialogOpen(true)}
              sx={{ mr: 2, borderRadius: '8px' }}
            >
              Publicar na OLX
            </Button>
          )}
          {canWrite && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditVehicle}
              sx={{ borderRadius: '8px' }}
            >
              Editar
            </Button>
          )}
        </Box>

        {canWrite && id && (
          <OlxPublishDialog open={olxDialogOpen} vehicleId={id} onClose={() => setOlxDialogOpen(false)} />
        )}

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          {/* Cabeçalho com informações principais */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 80,
                height: 80,
                mr: 3,
              }}
              src={mainImageUrl}
            >
              {!mainImageUrl && <CarIcon sx={{ fontSize: 40 }} />}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                {vehicle.brand} {vehicle.model}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip
                  label={getStatusText(vehicle.status)}
                  sx={{
                    bgcolor: getStatusColor(vehicle.status) + '20',
                    color: getStatusColor(vehicle.status),
                    fontWeight: 'medium',
                  }}
                />
                {vehicle.is_consignado && (
                  <Chip
                    icon={<ConsignmentIcon sx={{ fontSize: 18 }} />}
                    label="Consignado"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontWeight: 'medium' }}
                  />
                )}
                <Typography variant="body1" color="text.secondary">
                  Ano: {vehicle.year}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Informações detalhadas */}
          <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
            Informações do Veículo
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <LabelIcon sx={{ mr: 1, fontSize: 20 }} />
                    Placa / identificação
                  </Typography>
                  {vehicle.identification ? (
                    <>
                      <Typography variant="h6">{vehicle.identification}</Typography>
                      {vehicle.plate ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Placa: {vehicle.plate}
                        </Typography>
                      ) : null}
                    </>
                  ) : (
                    <Typography variant="h6">
                      {vehicle.no_plate ? 'Sem placa (0 km ou não emplacado)' : (vehicle.plate || '—')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <SpeedIcon sx={{ mr: 1, fontSize: 20 }} />
                    {vehicle.hours?.trim() ? 'Horas de uso' : 'Quilometragem'}
                  </Typography>
                  <Typography variant="h6">
                    {vehicle.hours?.trim()
                      ? `${vehicle.hours.trim()} h`
                      : (formatKmDisplay(vehicle.km) || '—')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ColorIcon sx={{ mr: 1, fontSize: 20 }} />
                    Cor
                  </Typography>
                  <Typography variant="h6">{vehicle.color || '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <DateIcon sx={{ mr: 1, fontSize: 20 }} />
                    Data de Compra
                  </Typography>
                  <Typography variant="h6">{formatDate(vehicle.purchase_date)}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
                    Preço de Compra
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {vehicle.is_consignado
                      ? 'N/A'
                      : (vehicle.purchase_price != null
                        ? formatCurrency(Number(vehicle.purchase_price))
                        : '—')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
                    Preço de Venda
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {vehicle.sale_price != null && vehicle.sale_price !== 0
                      ? formatCurrency(Number(vehicle.sale_price))
                      : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
                    Preço Promocional
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {vehicle.sale_price_promo != null && vehicle.sale_price_promo !== 0
                      ? formatCurrency(Number(vehicle.sale_price_promo))
                      : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
                    Tipo de veículo
                  </Typography>
                  <Typography variant="h6">{vehicle.vehicleType?.trim() || '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ChassisIcon sx={{ mr: 1, fontSize: 20 }} />
                    Chassi
                  </Typography>
                  <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                    {vehicle.chassis?.trim() || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <FipeIcon sx={{ mr: 1, fontSize: 20 }} />
                    Código FIPE
                  </Typography>
                  <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                    {vehicle.codigo_fipe?.trim() || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ModelYearIcon sx={{ mr: 1, fontSize: 20 }} />
                    Ano modelo (FIPE)
                  </Typography>
                  <Typography variant="h6">
                    {vehicle.codigo_ano != null && vehicle.codigo_ano !== ''
                      ? String(vehicle.codigo_ano)
                      : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <GearIcon sx={{ mr: 1, fontSize: 20 }} />
                    Câmbio
                  </Typography>
                  <Typography variant="h6">{vehicle.cambio?.trim() || '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <MotorIcon sx={{ mr: 1, fontSize: 20 }} />
                    Motor
                  </Typography>
                  <Typography variant="h6">{vehicle.motor?.trim() || '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>

          </Grid>

          {vehicle.internal_notes?.trim() && (
            <>
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Observações internas
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography whiteSpace="pre-wrap">{vehicle.internal_notes}</Typography>
              </Paper>
            </>
          )}

          {vehicle.notes?.trim() && (
            <>
              <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicNoteIcon fontSize="small" /> Observações
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography whiteSpace="pre-wrap">{vehicle.notes}</Typography>
              </Paper>
            </>
          )}

          {isMaintenance && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h6" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon /> Checklist de manutenção
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => void downloadFutureMaintenanceExcel()}
                    disabled={downloadingMaintenanceExcel}
                    sx={{ borderRadius: '8px' }}
                  >
                    {downloadingMaintenanceExcel ? 'Gerando Excel...' : 'Baixar Excel (futuras)'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => void downloadFutureMaintenancePdf()}
                    disabled={downloadingMaintenancePdf}
                    sx={{ borderRadius: '8px' }}
                  >
                    {downloadingMaintenancePdf ? 'Gerando PDF...' : 'Baixar PDF (futuras)'}
                  </Button>
                  {canWrite && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openNewMaintenance} sx={{ borderRadius: '8px' }}>
                      Novo item
                    </Button>
                  )}
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Itens do que precisa ser feito no carro.
                {isAdmin ? ' Você pode converter um item em despesa quando for executar/pagar.' : ''}
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Prioridade</TableCell>
                      <TableCell align="right">Estimativa</TableCell>
                      <TableCell align="center" width={200}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {maintenanceItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          Nenhum item cadastrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      maintenanceItems.map((it) => (
                        <TableRow key={it.id} hover>
                          <TableCell>
                            <Typography fontWeight="bold">{it.description}</Typography>
                            {it.internal_notes ? (
                              <Typography variant="body2" color="text.secondary">
                                Obs.: {it.internal_notes}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{it.status}</TableCell>
                          <TableCell>{it.priority}</TableCell>
                          <TableCell align="right">
                            {it.estimated_amount != null
                              ? it.estimated_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                              : '—'}
                          </TableCell>
                          <TableCell align="center">
                            {isAdmin && (
                              <Button size="small" onClick={() => convertToExpense(it)} sx={{ mr: 1 }}>
                                Criar despesa
                              </Button>
                            )}
                            {canWrite && (
                              <>
                                <IconButton size="small" onClick={() => openEditMaintenance(it)} aria-label="Editar">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => void deleteMaintenance(it)} aria-label="Excluir">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Dialog open={maintenanceDialogOpen} onClose={closeMaintenanceDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingMaintenanceItem ? 'Editar item' : 'Novo item'}</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                      label="Descrição"
                      value={maintenanceForm.description}
                      onChange={(e) => setMaintenanceForm((p) => ({ ...p, description: e.target.value }))}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Estimativa (R$)"
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      value={maintenanceForm.estimated_amount}
                      onChange={(e) => setMaintenanceForm((p) => ({ ...p, estimated_amount: e.target.value }))}
                      fullWidth
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={maintenanceForm.status}
                        label="Status"
                        onChange={(e) => setMaintenanceForm((p) => ({ ...p, status: e.target.value as MaintenanceItemStatus }))}
                      >
                        <MenuItem value="pending">Pendente</MenuItem>
                        <MenuItem value="in_progress">Em andamento</MenuItem>
                        <MenuItem value="done">Concluído</MenuItem>
                        <MenuItem value="cancelled">Cancelado</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Prioridade</InputLabel>
                      <Select
                        value={maintenanceForm.priority}
                        label="Prioridade"
                        onChange={(e) => setMaintenanceForm((p) => ({ ...p, priority: e.target.value as MaintenancePriority }))}
                      >
                        <MenuItem value="low">Baixa</MenuItem>
                        <MenuItem value="medium">Média</MenuItem>
                        <MenuItem value="high">Alta</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Observações internas (opcional)"
                      value={maintenanceForm.internal_notes}
                      onChange={(e) => setMaintenanceForm((p) => ({ ...p, internal_notes: e.target.value }))}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeMaintenanceDialog} disabled={savingMaintenance}>Cancelar</Button>
                  <Button variant="contained" onClick={() => void saveMaintenance()} disabled={savingMaintenance || !maintenanceForm.description.trim()}>
                    Salvar
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {documents.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileIcon /> Documentos anexados
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {documents.map((doc: any, idx: number) => {
                  const rawUrl = doc.path || doc.url;
                  if (!rawUrl) return null;
                  const fullUrl = getDocumentFullUrl(rawUrl);
                  const iconType = getDocumentIcon(rawUrl, doc.type);
                  const label = doc.type ? String(doc.type).replace(/_/g, ' ') : (doc.file_name || `Documento ${idx + 1}`);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={doc.id ?? idx}>
                      <Card
                        sx={{
                          borderRadius: '12px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: 4 },
                        }}
                      >
                        <Box
                          sx={{
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          }}
                        >
                          {iconType === 'pdf' && (
                            <PdfIcon sx={{ fontSize: 56, color: theme.palette.error.main }} />
                          )}
                          {iconType === 'image' && (
                            <ImageIcon sx={{ fontSize: 56, color: theme.palette.info.main }} />
                          )}
                          {iconType === 'file' && (
                            <FileIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />
                          )}
                        </Box>
                        <CardContent sx={{ flex: 1, pt: 1.5, pb: 1.5 }}>
                          <Typography variant="subtitle2" noWrap title={label} sx={{ fontWeight: 600, mb: 1 }}>
                            {label}
                          </Typography>
                          <Button
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            fullWidth
                            startIcon={<OpenInNewIcon />}
                          >
                            Abrir documento
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Dados da consignação */}
          {vehicle.is_consignado && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConsignmentIcon /> Dados da consignação
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {vehicle.consignment_commission != null && vehicle.consignment_commission > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Comissão</Typography>
                        <Typography variant="h6">
                          {isConsignmentCommissionFixed(vehicle.consignment_commission_type)
                            ? formatCurrency(vehicle.consignment_commission)
                            : `${vehicle.consignment_commission}%`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {vehicle.consignment_exposure && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Exposição</Typography>
                        <Typography variant="h6">
                          {CONSIGNMENT_EXPOSURE_LABELS[vehicle.consignment_exposure] || vehicle.consignment_exposure}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {vehicle.consignment_contract_date && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Data do contrato</Typography>
                        <Typography variant="h6">{formatDate(vehicle.consignment_contract_date)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {vehicle.consignment_owner_name && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 0.5, fontSize: 18 }} /> Proprietário
                        </Typography>
                        <Typography variant="h6">{vehicle.consignment_owner_name}</Typography>
                        {vehicle.consignment_owner_contact && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon sx={{ mr: 0.5, fontSize: 16 }} /> {vehicle.consignment_owner_contact}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {vehicle.consignment_notes && (
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: '12px' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Observações da consignação</Typography>
                        <Typography variant="body1">{vehicle.consignment_notes}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ContractIcon />}
                  onClick={() => navigate(`/contratos/consignacao/${vehicle.id}`)}
                  sx={{ borderRadius: '8px' }}
                >
                  Gerar contrato de consignação
                </Button>
              </Box>
            </>
          )}

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            {isAdmin && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<MoneyIcon />}
                onClick={handleViewExpenses}
                sx={{ borderRadius: '8px' }}
              >
                Gerenciar Despesas
              </Button>
            )}
            {canWrite && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEditVehicle}
                sx={{ borderRadius: '8px' }}
              >
                Editar Veículo
              </Button>
            )}
          </Box>
        </Paper>
      </div>
    </Fade>
  );
};

export default VehicleDetails; 